import React, { useState, useContext, useEffect } from 'react';

import set from 'lodash/set';
import { Button } from '@material-ui/core';
import AppContext from '../context/AppContext';
import { changeSpinnerValue, importData, setResumeItems } from '../utils';
import ItemHeading from './ItemHeading';
import Form from './Form';
import CancelFormDialog from './CancelFormDialog';
import { createQualification, getUserData } from '../services/api';
import { errorToast, successToast } from './toast';
import { normalizeQualificationTypeForComparison } from '../constants/qualificationTypes';

const normalizeQualificationKey = (item) => {
  const rawType = (item.type || item.major || '').toString().trim();
  const type = normalizeQualificationTypeForComparison(rawType);
  const name = (item.name || item.description || '').toString().trim();
  const issuedBy = (item.issuedBy || item.issued || '').toString().trim();
  return { type, name, issuedBy };
};

const INITIAL_QUALIFICATION_ITEM = {
  status: 'PENDING',
  type: null,
  name: '',
  credentialLink: '',
  issuedBy: '',
  stateAccredited: false,
  accreditationCountry: '',
  issueDate: null,
  expirationDate: null,
  skills: [],
  file: null,
};

const INITIAL_ERRORS = {
  type: false,
  name: false,
  credentialLink: false,
  issuedBy: false,
  issueDate: false,
  accreditationCountry: false,
  file: false,
  key: false,
  major: false,
  link: false,
  exists: false,
  country: false,
  qualification: false,
  issued: false,
  start: false,
};

const AddItem = ({ heading, dispatch, existingItems }) => {
  const context = useContext(AppContext);
  const { state } = context;
  const { ownUserInfo, selectedCvId, ownCvId } = state;
  const { personId } = ownUserInfo;
  const userId = ownUserInfo?.userId;

  const [isOpen, setOpen] = useState(false);
  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [item, setItem] = useState(INITIAL_QUALIFICATION_ITEM);

  const onChange = (key, value) => setItem((prev) => set({ ...prev }, key, value));

  const currentKey = normalizeQualificationKey(item);
  useEffect(() => {
    if (!currentKey.type || !currentKey.name || !currentKey.issuedBy) {
      setErrors((prev) => ({ ...prev, issued: false }));
      return;
    }
    const isDuplicate = existingItems.some((existing) => {
      const k = normalizeQualificationKey(existing);
      return (
        k.type === currentKey.type &&
        k.name === currentKey.name &&
        k.issuedBy === currentKey.issuedBy
      );
    });
    setErrors((prev) => ({ ...prev, exists: isDuplicate }));
  }, [
    item.type,
    item.major,
    item.name,
    item.description,
    item.issuedBy,
    item.issued,
    existingItems,
  ]);

  const refreshUserData = async () => {
    if (!userId) return;
    try {
      const res = await getUserData(userId);
      if (res?.length) {
        setResumeItems(dispatch, res);
        const currentCvItem =
          res.find((r) => r.id === selectedCvId) ??
          res.find((r) => r.id === ownCvId) ??
          res.find((r) => r.isMain) ??
          res[0];
        if (currentCvItem) {
          importData(dispatch, { data: { ...currentCvItem } });
        }
      }
    } catch {
      errorToast('Error refreshing user data');
    }
  };

  const validateRequiredFields = () => {
    const typeEmpty = !item.type;
    const nameEmpty = !(item.name || item.description || '').toString().trim();
    const issuedByEmpty = !(item.issuedBy || item.issued || '').toString().trim();
    const issueDateEmpty = !item.issueDate;
    const countryEmpty = item.stateAccredited && !item.accreditationCountry;
    const hasErrors = typeEmpty || nameEmpty || issuedByEmpty || issueDateEmpty || countryEmpty;

    if (hasErrors) {
      setErrors((prev) => ({
        ...prev,
        type: typeEmpty,
        name: nameEmpty,
        issued: issuedByEmpty,
        issuedBy: issuedByEmpty,
        start: issueDateEmpty,
        issueDate: issueDateEmpty,
        accreditationCountry: countryEmpty,
      }));
    }
    return !hasErrors;
  };

  const onSubmit = async () => {
    if (!validateRequiredFields()) {
      setHasTriedSubmit(true);
      return;
    }
    try {
      setIsLoading(true);
      changeSpinnerValue(dispatch, true);
      await createQualification(personId, item);
      await refreshUserData();
      successToast('Request to update education has been submitted.');
      setItem(INITIAL_QUALIFICATION_ITEM);
      setErrors(INITIAL_ERRORS);
      setOpen(false);
    } catch {
      errorToast();
    } finally {
      setIsLoading(false);
      changeSpinnerValue(dispatch, false);
    }
  };

  const handleClose = () => {
    setItem(INITIAL_QUALIFICATION_ITEM);
    setErrors(INITIAL_ERRORS);
    setIsOpenCancelModal(false);
    setOpen(false);
  };

  const isItemEmpty = (itemCheck) => {
    return (
      !itemCheck.type &&
      !itemCheck.name &&
      !itemCheck.credentialLink &&
      !itemCheck.issuedBy &&
      !itemCheck.stateAccredited &&
      !itemCheck.accreditationCountry &&
      (!itemCheck.skills || itemCheck.skills.length === 0) &&
      !itemCheck.file &&
      !itemCheck.issueDate &&
      !itemCheck.expirationDate
    );
  };

  const isRequiredFieldsEmpty = (itemCheck) => {
    return !itemCheck.type || !itemCheck.name || !itemCheck.issuedBy || !itemCheck.issueDate;
  };

  const onCancel = () => {
    if (isItemEmpty(item)) {
      setOpen(false);
    } else {
      setIsOpenCancelModal(true);
    }
  };

  return (
    <div className="my-4 border border-gray-200 rounded">
      <ItemHeading heading={heading} setOpen={setOpen} isOpen={isOpen} />

      <div className={`${isOpen ? 'block' : 'hidden'} px-5 pb-5`}>
        <Form
          item={item}
          onChange={onChange}
          errors={errors}
          setErrors={setErrors}
          isOpen={isOpen}
        />

        <div className="flex gap-2 w-full justify-end">
          <Button
            variant="outlined"
            color="primary"
            onClick={onCancel}
            style={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={isLoading || errors.exists}
            style={{ textTransform: 'none' }}
          >
            Submit for review
          </Button>
        </div>
      </div>

      <CancelFormDialog
        open={isOpenCancelModal}
        onClose={handleClose}
        setIsOpenCancelModal={setIsOpenCancelModal}
      />
    </div>
  );
};

export default AddItem;
